import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, FormControl, Grid, MenuItem, Select, Slider } from '@material-ui/core';
import { RUBRIC_GROUPS } from '../../../constants/scoring';
import ScoringModules from './ScoringModules';

const styles = makeStyles((theme) => ({
  sectionSpacer: {
    height: 50,
    padding: '10px 0',
  },
  rowSpacer: {
    height: 30,
  },
  textRowSpacer: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
  },
  [theme.breakpoints.down('xs')]: {
    textField: {
      fontSize: '0.75rem',
    },
    root: {
      '& .MuiFormLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  printedContent: {
    '@media print': {
      marginBottom: 10,
    },
    '@media screen': {
      display: 'none',
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  alignPrintedContent: {
    lineHeight: '25px',
    verticalAlign: 'top',
  },
  rubricTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
  },
  rubricQuestion: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
    fontSize: '0.90rem',
    fontWeight: 600,
  },
  rubricExplanation: {
    fontSize: '0.90rem',
    fontStyle: 'italic',
    height: 80,
  },
  rubricScore: {
    [theme.breakpoints.up('sm')]: {
      height: 30,
    },
    fontSize: '1.05rem',
    fontWeight: 400,
  },
}));

export default function Scoring(props) {
  const { form, setForm, disabled } = props;

  const classes = styles();

  const housingRubricMarks = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 2,
      label: '2',
    },
    {
      value: 3,
      label: '3',
    },
    {
      value: 4,
      label: '4',
    },
  ];

  const lookupMotivationValue = (value) => {
    switch (value) {
      case 1:
        return 'Student demonstrates faint interest in working on their post-graduation plan, moving through the motions and completing most basic tasks and expectations with lots of prompts, motivators, and consequences, and showing no initiative or enthusiasm.';
      case 2:
        return 'Student demonstrates some interest in working on the post-graduation plan, completes most basic tasks and expectations with a few prompts, motivators, and consequences, and shows very little initiative or enthusiasm.';
      case 3:
        return 'Student demonstrates interest in working on the post-graduation plan, completes most basic tasks and expectations without prompts, motivators, consequences, has a positive attitude, and shows some initiative and enthusiasm.';
      case 4:
        return 'Student works on their plan with persistence, a positive attitude, self-discipline, and lots of initiative and enthusiasm asking for help and keeping track of all requirements independently.';
      default:
        return '';
    }
  };

  const lookupFutureValue = (value) => {
    switch (value) {
      case 1:
        return 'Student refuses to set a realistic post-graduation plan.';
      case 2:
        return "Student has an unrealistic view of their abilities and post-graduation plans as evidenced by contradictory MAP, ACT, and ASVAB scores, the team's overall unfavorable support, and student is reluctant to feedback received during the Sr. Planning sessions.";
      case 3:
        return 'Student is somewhat realistic about their abilities and post-graduation plans as evidenced by supportive MAP, ACT, and ASVAB scores and team-neutral support, student is open to feedback received during the Sr. Planning sessions.';
      case 4:
        return 'Student is realistic about their abilities and plans for their future as evidenced by supportive MAP, ACT, and ASVAB scores and team overall favorable support.';
      default:
        return '';
    }
  };

  const lookupSFTeamValue = (value) => {
    switch (value) {
      case 1:
        return 'Never: Student does not reach out via email, call, or attend afterschool hours.';
      case 2:
        return 'Sometimes: Student reaches out to SFS only when facing consequences or past due on their requirements.';
      case 3:
        return 'Often: Student reaches out to SFS with proactive questions about their senior planning binder completion and some about their post-graduation plan.';
      case 4:
        return 'Very Often: Student reaches out to SFS with proactive questions about their post-graduation plan and senior planning binder completion. Communication is fluid and allows for relationship building.';
      default:
        return '';
    }
  };

  const handleRubricMotivationSlider = (ev, value) => {
    console.log('................in handleRubricMotivationSlider: ', value, form.rubricFuture, form.rubricSFTeam);
    const explain = lookupMotivationValue(value);
    const avg = (value + form.rubricFuture + form.rubricSFTeam) / 3;
    setForm({ ...form, rubricMotivation: value, rubricMotivationExplain: explain, rubricMotivationScore: avg });
  };

  const handleRubricFutureSlider = (ev, value) => {
    console.log('................in handleRubricFutureSlider: ', value, form.rubricMotivation, form.rubricSFTeam);
    const explain = lookupFutureValue(value);
    const avg = (form.rubricMotivation + value + form.rubricSFTeam) / 3;
    setForm({ ...form, rubricFuture: value, rubricFutureExplain: explain, rubricMotivationScore: avg });
  };

  const handleRubricSFTeamSlider = (ev, value) => {
    console.log('................in handleRubricSFTeamSlider: ', value, form.rubricMotivation, form.rubricFuture);
    const explain = lookupSFTeamValue(value);
    const avg = (form.rubricMotivation + form.rubricFuture + value) / 3;
    setForm({ ...form, rubricSFTeam: value, rubricSFTeamExplain: explain, rubricMotivationScore: avg });
  };

  const renderHousingRubric = () => {
    return (
      <>
        <Grid container direction="row">
          <Grid item xs={12} align="center" style={{ margin: '25px 0 15px 0' }}>
            <div className={classes.rubricTitle}>Housing Rubric Enhancement</div>
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
            1. Displays self-motivation while working on their post-graduation plan (beyond the actual completion of the senior planner modules).
          </Grid>
          <Grid item xs={8} sm={6} md={3} lg={4} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Box sx={{ width: 300 }}>
                <Slider
                  aria-label="motivation-rubric"
                  value={form.rubricMotivation}
                  onChange={handleRubricMotivationSlider}
                  marks={housingRubricMarks}
                  step={1}
                  min={0}
                  max={4}
                />
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricExplanation} align="left">
            {form.rubricMotivationExplain && form.rubricMotivationExplain.length > 0 && form.rubricMotivationExplain}
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
            2. Thinking about their future (SFS)
          </Grid>
          <Grid item xs={8} sm={6} md={3} lg={4} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Box sx={{ width: 300 }}>
                <Slider
                  aria-label="future-rubric"
                  value={form.rubricFuture}
                  onChange={handleRubricFutureSlider}
                  marks={housingRubricMarks}
                  step={1}
                  min={0}
                  max={4}
                />
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricExplanation} align="left">
            {form.rubricFutureExplain && form.rubricFutureExplain.length > 0 && form.rubricFutureExplain}
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
            3. Student communication with SF team: Student is appropriately communicating (email, call, afterschool hours) with SF
          </Grid>
          <Grid item xs={8} sm={6} md={3} lg={4} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Box sx={{ width: 300 }}>
                <Slider
                  aria-label="sfteam-rubric"
                  value={form.rubricSFTeam}
                  onChange={handleRubricSFTeamSlider}
                  marks={housingRubricMarks}
                  step={1}
                  min={0}
                  max={4}
                />
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricExplanation} align="left">
            {form.rubricSFTeamExplain && form.rubricSFTeamExplain.length > 0 && form.rubricSFTeamExplain}
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
            4. Considering all available collateral information and professional judgment, as an SFS, how likely is this student to succeed with their
            current post-graduation plan?
          </Grid>
          <Grid item xs={6} sm={5} md={4} lg={3} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Select
                variant="outlined"
                labelId="likely-success-label"
                id="likely-success"
                value={form.rubricSuccess}
                onChange={(event) => setForm({ ...form, rubricSuccess: event.target.value })}
                displayEmpty
                className={classes.textFieldBackground}
              >
                <MenuItem value={0} disabled>
                  Select Likelihood for Success
                </MenuItem>
                <MenuItem value={1}>Highly unlikely</MenuItem>
                <MenuItem value={2}>Unlikely</MenuItem>
                <MenuItem value={3}>Likely</MenuItem>
                <MenuItem value={4}>Highly likely</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderModules = () => {
    if (form && form.version === 2) {
      return <ScoringModules rubricGroups={RUBRIC_GROUPS} form={form} setForm={setForm} disabled={disabled} classes={classes} />;
    }
    return (
      <>
        <Grid item xs={12} align="left">
          <label style={{ fontWeight: 600 }}>Preparation Sheet #1</label>
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <label>Sheet is fully completed by student.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <FormControl fullWidth>
            <Select
              labelId="sheet 1 fully completed"
              id="sheet-1-completed"
              value={form.gradingSheet1Completed}
              onChange={(event) => setForm({ ...form, gradingSheet1Completed: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={9}>Sheet was thoughtfully completed by student</MenuItem>
              <MenuItem value={6}>Sheet was completed by student but requires more work</MenuItem>
              <MenuItem value={3}>Sheet completion needs significant improvement</MenuItem>
              <MenuItem value={0}>Sheet was not completed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <label>Sheet is submitted on time.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <FormControl fullWidth>
            <Select
              labelId="sheet 1 submitted on time"
              id="sheet-1-submitted"
              value={form.gradingSheet1Submitted}
              onChange={(event) => setForm({ ...form, gradingSheet1Submitted: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={8}>Sheet was submitted on time</MenuItem>
              <MenuItem value={6}>Sheet was submitted late (within 3 days after initial meeting)</MenuItem>
              <MenuItem value={3}>Sheet was submitted significantly late (between 4 and 7 days after initial meeting)</MenuItem>
              <MenuItem value={0}>Sheet was not submitted</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 10 }}>
          <label>Student is able to clearly speak about her/his plan options and rationales.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 10 }}>
          <FormControl fullWidth>
            <Select
              labelId="sheet 1 clarity"
              id="sheet-1-clarity"
              value={form.gradingSheet1Clarity}
              onChange={(event) => setForm({ ...form, gradingSheet1Clarity: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={8}>Student showed clarity and confidence about his/her post-graduation plans</MenuItem>
              <MenuItem value={6}>Student was hesitant and vacillating about his/her post-graduation plan</MenuItem>
              <MenuItem value={0}>Student could not speak about her/his post-graduation plan</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600 }}>Preparation Sheet #1 Score</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          {(form.gradingSheet1Completed && form.gradingSheet1Completed >= 0 ? form.gradingSheet1Completed : 0) +
            (form.gradingSheet1Submitted && form.gradingSheet1Submitted >= 0 ? form.gradingSheet1Submitted : 0) +
            (form.gradingSheet1Clarity && form.gradingSheet1Clarity >= 0 ? form.gradingSheet1Clarity : 0)}
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} align="left">
          <label style={{ fontWeight: 600 }}>Module #1</label>
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <label>Module was submitted on time.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <FormControl fullWidth>
            <Select
              labelId="module 1 submitted"
              id="module-1-submitted"
              value={form.module1Submitted}
              onChange={(event) => setForm({ ...form, module1Submitted: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={10}>Module was submitted on time</MenuItem>
              <MenuItem value={6}>Module was submitted late (within 3 days after initial meeting)</MenuItem>
              <MenuItem value={3}>Module was submitted significantly late (between 4 and 7 days after initial meeting)</MenuItem>
              <MenuItem value={0}>Module was not submitted</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} align="left"></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 15 }}>
          <label>Tasks / activities thoughtfully completed with realistic examples and clear statements.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 15 }}>
          <FormControl fullWidth>
            <Select
              labelId="module 1 thoughtfully completed"
              id="module-1-completed"
              value={form.module1Completed}
              onChange={(event) => setForm({ ...form, module1Completed: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={15}>Module was thoughtfully completed by student</MenuItem>
              <MenuItem value={10}>Module was completed by student but requires more work</MenuItem>
              <MenuItem value={5}>Module completion needs significant improvement</MenuItem>
              <MenuItem value={0}>Module was not completed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600 }}>Module #1 Score</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          {(form.module1Submitted && form.module1Submitted >= 0 ? form.module1Submitted : 0) +
            (form.module1Completed && form.module1Completed >= 0 ? form.module1Completed : 0)}
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} align="left">
          <label style={{ fontWeight: 600 }}>Module #2</label>
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <label>Module was submitted on time.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <FormControl fullWidth>
            <Select
              labelId="module 2 submitted"
              id="module-2-submitted"
              value={form.module2Submitted}
              onChange={(event) => setForm({ ...form, module2Submitted: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={10}>Module was submitted on time</MenuItem>
              <MenuItem value={6}>Module was submitted late (within 3 days after initial meeting)</MenuItem>
              <MenuItem value={3}>Module was submitted significantly late (between 4 and 7 days after initial meeting)</MenuItem>
              <MenuItem value={0}>Module was not submitted</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} align="left"></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 35 }}>
          <label>
            Worksheets and activities are thoughtfully completed with realistic exploration of options and clear notes and follow-up descriptions.
          </label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 35 }}>
          <FormControl fullWidth>
            <Select
              labelId="module 2 thoughtfully completed"
              id="module-2-completed"
              value={form.module2Completed}
              onChange={(event) => setForm({ ...form, module2Completed: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={15}>Module was thoughtfully completed by student</MenuItem>
              <MenuItem value={10}>Module was completed by student but requires more work</MenuItem>
              <MenuItem value={5}>Module completion needs significant improvement</MenuItem>
              <MenuItem value={0}>Module was not completed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600 }}>Module #2 Score</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          {(form.module2Submitted && form.module2Submitted >= 0 ? form.module2Submitted : 0) +
            (form.module2Completed && form.module2Completed >= 0 ? form.module2Completed : 0)}
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} align="left">
          <label style={{ fontWeight: 600 }}>Module #3</label>
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <label>Module was submitted on time.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <FormControl fullWidth>
            <Select
              labelId="module 3 submitted"
              id="module-3-submitted"
              value={form.module3Submitted}
              onChange={(event) => setForm({ ...form, module3Submitted: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={10}>Module was submitted on time</MenuItem>
              <MenuItem value={6}>Module was submitted late (within 3 days after initial meeting)</MenuItem>
              <MenuItem value={3}>Module was submitted significantly late (between 4 and 7 days after initial meeting)</MenuItem>
              <MenuItem value={0}>Module was not submitted</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} align="left"></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 15 }}>
          <label>Activities (budget, best bank account, etc.) were thoughtfully completed with realistic examples and clear statements.</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 15 }}>
          <FormControl fullWidth>
            <Select
              labelId="module 3 thoughtfully completed"
              id="module-3-completed"
              value={form.module3Completed}
              onChange={(event) => setForm({ ...form, module3Completed: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value={15}>Module was thoughtfully completed by student</MenuItem>
              <MenuItem value={10}>Module was completed by student but requires more work</MenuItem>
              <MenuItem value={5}>Module completion needs significant improvement</MenuItem>
              <MenuItem value={0}>Module was not completed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600 }}>Module #3 Score</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
          {(form.module3Submitted && form.module3Submitted >= 0 ? form.module3Submitted : 0) +
            (form.module3Completed && form.module3Completed >= 0 ? form.module3Completed : 0)}
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          <label style={{ fontWeight: 600 }}>Overall Total Score</label>
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
          {(form.gradingSheet1Completed && form.gradingSheet1Completed >= 0 ? form.gradingSheet1Completed : 0) +
            (form.gradingSheet1Submitted && form.gradingSheet1Submitted >= 0 ? form.gradingSheet1Submitted : 0) +
            (form.gradingSheet1Clarity && form.gradingSheet1Clarity >= 0 ? form.gradingSheet1Clarity : 0) +
            (form.module1Submitted && form.module1Submitted >= 0 ? form.module1Submitted : 0) +
            (form.module1Completed && form.module1Completed >= 0 ? form.module1Completed : 0) +
            (form.module2Submitted && form.module2Submitted >= 0 ? form.module2Submitted : 0) +
            (form.module2Completed && form.module2Completed >= 0 ? form.module2Completed : 0) +
            (form.module3Submitted && form.module3Submitted >= 0 ? form.module3Submitted : 0) +
            (form.module3Completed && form.module3Completed >= 0 ? form.module3Completed : 0)}
        </Grid>
        <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      </>
    );
  };

  return (
    <>
      <Grid item xs={12} align="center" style={{ margin: '15px 0' }}>
        <label style={{ fontWeight: 600 }}>Grading the Planning Meeting Form 1</label>
      </Grid>
      {renderModules()}
      {renderHousingRubric()}
    </>
  );
}

Scoring.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  disabled: PropTypes.bool,
};
